import BaseModel from './BaseModel'
import axiosInstance from 'api/axios.config'
import { USER_FETCHED } from '../redux/actions/users'
import snakecase from 'snakecase-keys'
import { fk, many } from 'redux-orm'
import {
  FINISH_CREATE_NEW_PLAYLIST,
  FETCH_PLAYLISTS_SUCCESS,
  SET_DISPLAYED_PLAYLIST,
  PLAYLIST_FEATURED,
  PLAYLIST_UNFEATURED,
  PLAYLIST_UPDATED,
  PLAYLIST_DELETED,
  REORDER_USER_PLAYLISTS
} from '../redux/actions/playlists'

class Playlist extends BaseModel {
  static reducer (action, Playlist, session) {
    const currentUser = session.User.first()
    let currentPlaylists, playlist

    switch (action.type) {
      case USER_FETCHED:
        action.user.followedPlaylists.forEach((playlist, order) => {
          playlist = {
            ...playlist,
            order,
            user: action.user.username
          }
          Playlist.upsert(playlist)
        })
        break
      case FETCH_PLAYLISTS_SUCCESS:
        currentPlaylists = Playlist.all()
        if (action.reloadAll && currentPlaylists.count() > 0) {
          currentPlaylists.delete()
        }
        action.playlists.forEach(playlist => {
          Playlist.create(playlist)
        })
        break
      case FINISH_CREATE_NEW_PLAYLIST:
        currentPlaylists = Playlist.all().toModelArray()
        currentPlaylists.forEach(playlist => {
          playlist.set('order', playlist.order + 1)
        })
        playlist = {
          ...action.playlist,
          order: 0,
          user: currentUser.getId()
        }
        const newPlaylist = Playlist.create(playlist)
        if (action.firstPlanEntryId) {
          newPlaylist.planEntries.add(action.firstPlanEntryId)
        }

        break
      case SET_DISPLAYED_PLAYLIST:
        Playlist.upsert(action.playlist)
        break
      case PLAYLIST_FEATURED:
        playlist = Playlist.withId(action.uuid)
        playlist.update({
          featured: true
        })
        break
      case PLAYLIST_UNFEATURED:
        playlist = Playlist.withId(action.uuid)
        playlist.update({
          featured: false
        })
        break
      case PLAYLIST_UPDATED:
        playlist = Playlist.withId(action.uuid)
        playlist.update(action.params)
        break
      case PLAYLIST_DELETED:
        playlist = Playlist.withId(action.uuid)
        playlist.delete()
        break
      case REORDER_USER_PLAYLISTS:
        action.order.forEach((playlistId, idx) => {
          const playlist = Playlist.get({ id: parseInt(playlistId) })

          if (playlist && playlist.order !== idx) {
            playlist.update({ order: idx })
          }
        })
    }
  }

  static createNew (title, planEntryId) {
    return axiosInstance.post(`/playlists`, {
      title,
      plan_entry_id: planEntryId
    })
  }

  static sortUserPlaylists (order) {
    return axiosInstance.post('/playlists/sort-user-playlists', {
      playlists: order
    })
  }

  toggleFavorites () {
    if (this.following) {
      return this.unfollowPlaylist()
    } else {
      return this.followPlaylist()
    }
  }

  imageUrl () {
    if (this.coverPhoto.small.url) {
      return this.coverPhoto.small.url
    }
    if (this.firstEntry?.videoThumbnail) {
      return this.firstEntry.videoThumbnail
    }
    return 'https://d28gwrkukqy4h7.cloudfront.net/playlists/playlist-default.png'
  }

  url () {
    return `/playlists/${this.uuid}`
  }

  duplicate () {
    return axiosInstance.post(`${this.url()}/duplicate`)
  }

  destroy () {
    return axiosInstance.delete(this.url())
  }

  feature () {
    return axiosInstance.post(`${this.url()}/feature`)
  }

  unfeature () {
    return axiosInstance.delete(`${this.url()}/feature`)
  }

  report () {
    return axiosInstance.post(`${this.url()}/report`)
  }

  fetchPlanEntries (params) {
    return axiosInstance.get(`${this.url()}/entries`, { params })
  }

  sort (planEntryIds) {
    axiosInstance.post(`${this.url()}/sort`, {
      plan_entry: planEntryIds
    })
  }

  updateBackend (params) {
    return axiosInstance.put(this.url(), snakecase(params))
  }
}

Playlist.modelName = 'Playlist'
Playlist.apiPath = '/playlists'

Playlist.options = {
  idAttribute: 'uuid'
}

Playlist.fields = {
  user: fk('User', 'playlists'),
  planEntries: many({
    to: 'PlanEntry',
    through: 'PlaylistEntries',
    relatedName: 'playlists'
  })
}

export default Playlist
